// Words. The Commander register (SPEC §16) and the katakana secondary labels (§15).
//
// Two rules from the spec, and they pull in opposite directions on purpose:
//
//	§16  "Second person throughout, and the address is Commander. Not 'player',
//	     not 'user', never a username." Every string this file produces for the
//	     HUD is an order or a report, never app copy.
//
//	§15  "Secondary katakana labels beside primary Latin ones for section headers
//	     and status lines... NEVER for user content: player comments, country
//	     names, and anything a person wrote render in a clean sans, unstyled."
//
// So the katakana table below is keyed by SECTION, not by string. There is no
// general-purpose "translate this to katakana" helper here, because the moment
// one exists somebody will run a player's comment through it.

/** Section headers. Latin primary, katakana secondary — the film's own habit. */
export const SectionLabels = {
  bank: { latin: 'BANK', kana: 'バンク' },
  feed: { latin: 'FIELD REPORT', kana: 'センジョウホウコク' },
  target: { latin: 'TARGET', kana: 'ヒョウテキ' },
  audio: { latin: 'AUDIO', kana: 'オンキョウ' },
  link: { latin: 'LINK', kana: 'リンク' },
  motion: { latin: 'MOTION', kana: 'モーション' },
  outcome: { latin: 'LAST ORDER', kana: 'サイシュウメイレイ' },
} as const;

export type SectionKey = keyof typeof SectionLabels;

/** Connection state, in the register. */
export const LinkLabels: Record<string, string> = {
  idle: 'STANDBY',
  connecting: 'LINKING',
  handshaking: 'AUTHENTICATING',
  live: 'LINK ESTABLISHED',
  closed: 'LINK LOST',
};

/**
 * The three failure outcomes, named so they cannot be confused (§3).
 *
 * The words matter as much as the colours: "REFUSED" and "OVERRUN" and "NOT SENT"
 * are three different events, and a player who sees three flashes and one word
 * has learned nothing.
 */
export const OutcomeLabels: Record<string, { title: string; detail: string }> = {
  pending: { title: 'ORDER SENT', detail: 'awaiting confirmation' },
  applied: { title: 'ROUNDS ON TARGET', detail: 'damage stands' },
  captured: { title: 'PIXEL TAKEN', detail: 'killing blow' },
  illegal: { title: 'ORDER REFUSED', detail: 'click returned to your bank' },
  lost: { title: 'OVERRUN', detail: 'another Commander landed the blow first' },
  dropped: { title: 'NOT SENT', detail: 'never reached the board; refunded' },
  settled: { title: 'SUPERSEDED', detail: 'the board already has the answer' },
};

/** The refusals the client settles before a round trip. */
export const RefusalLabels: Record<string, string> = {
  'no-budget': 'Bank empty, Commander. Hold.',
  'no-country': 'No country assigned. Awaiting placement.',
  'off-board': 'Off the map.',
  'not-adjacent': 'Not adjacent to your territory.',
};

/**
 * The country name registry.
 *
 * SPEC §15 is explicit that "the hovered pixel always names its owner in text",
 * and nothing on the wire carries a name — WELCOME carries a country ID and the
 * board carries country IDs, and there is no names endpoint. This table is the
 * client-side stand-in, seeded from §10's launch list, and it is REPLACEABLE by
 * design: `setCountryNames` takes whatever the server eventually serves.
 *
 * The fallback is never blank. An unnamed country still gets "COUNTRY 41",
 * because §15's accessibility clause is about not resting identity on colour, and
 * an empty label rests it on colour exactly as hard as no label at all.
 */
const names = new Map<number, string>([
  [0, 'NEUTRAL'],
  [1, 'BRAZIL'],
  [2, 'POLAND'],
  [3, 'TURKEY'],
  [4, 'PHILIPPINES'],
  [5, 'INDONESIA'],
  [6, 'PORTUGAL'],
  [7, 'MEXICO'],
  [8, 'GREECE'],
]);

export function setCountryNames(table: Iterable<readonly [number, string]>): void {
  for (const [id, name] of table) names.set(id, name);
}

export function countryName(id: number): string {
  return names.get(id) ?? `COUNTRY ${id}`;
}

/** Whether the id has a real name or only the generated fallback. */
export function countryNamed(id: number): boolean {
  return names.has(id);
}

/**
 * A feed line, in the Commander register.
 *
 * The wire carries a `text` body for lines the server wrote; where it is empty
 * this composes one from the structured fields, which is what keeps the feed
 * readable when the server is only sending kinds and ids.
 */
export function feedText(line: {
  kind: number;
  country: number;
  other: number;
  text: string;
}): string {
  if (line.text) return line.text;
  const who = countryName(line.country);
  const other = countryName(line.other);
  switch (line.kind) {
    case 0:
      return `${who} has taken ground from ${other}.`;
    case 1:
      return `${who} has lost a pixel to ${other}.`;
    case 2:
      return `Fortress under attack. ${who} is under siege.`;
    case 3:
      return `Fortress destroyed. ${who} has lost the position.`;
    case 4:
      return `${who} has been eliminated.`;
    case 5:
      return `${who} has opened a beachhead.`;
    case 6:
      return 'Season freeze imminent, Commander.';
    default:
      return `${who}.`;
  }
}
